import React, { useCallback, useEffect, useState } from "react";
import { FaCog, FaPlus, FaTrash, FaEdit, FaExternalLinkAlt } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { apihubDelete, apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const emptyForm = () => ({
  sheetname: "",
  sheeturl: "",
  spreadsheetid: "",
  description: "",
});

const GoogleSheetConfig = () => {
  const { projectId } = useProjectPhone();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubPost(
        `/getInteractedCustomerDetail/fetchallgooglesheetconfig?page=${page}&limit=${limit}`,
        { projectId },
      );
      if (res?.success) {
        setRows(res.data || []);
        setTotalPages(res.pagination?.totalPages || 1);
      } else {
        setRows([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch Google Sheet configs");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const result = location.state?.googleSheet;
    if (!result) return;
    if (result === "created") {
      setMessage("Google Sheet created successfully.");
      setError("");
      load();
    } else if (result === "denied") {
      setError("Google authorization was cancelled.");
    } else {
      setError(location.state?.message || "Failed to create Google Sheet.");
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [load, location.pathname, location.state, navigate]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setModalOpen(true);
    setMessage("");
    setError("");
  };

  const openEdit = (row) => {
    setEditId(row._id);
    setForm({
      sheetname: row.sheetname || "",
      sheeturl: row.sheeturl || "",
      spreadsheetid: row.spreadsheetid || "",
      description: row.description || "",
    });
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.sheetname) {
      setError("Please enter a sheet name.");
      return;
    }
    if (editId && !form.sheeturl) {
      setError("Please fill required fields (sheet name, sheet URL).");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (editId) {
        const res = await apihubPost(
          "/getInteractedCustomerDetail/editgooglesheetconfig",
          {
            projectid: projectId,
            activeeditid: editId,
            sheetname: form.sheetname,
            sheeturl: form.sheeturl,
            spreadsheetid: form.spreadsheetid,
            description: form.description,
          },
        );
        if (res?.success === false) {
          throw new Error(res.message || "Save failed");
        }
        setMessage(res?.message || "Config updated");
        setModalOpen(false);
        setForm(emptyForm());
        setEditId(null);
        load();
        return;
      }

      const redirectUri = `${window.location.origin}/google-oauth-callback`;
      const res = await apihubPost(
        "/getInteractedCustomerDetail/googlesheetoauthstart",
        {
          projectId,
          sheetname: form.sheetname,
          description: form.description,
          redirectUri,
          returnTo: redirectUri,
        },
      );
      if (res?.authUrl) {
        window.location.assign(res.authUrl);
        return;
      }
      if (res?.success === false) {
        throw new Error(res.message || "Failed to create Google Sheet");
      }
      setMessage(res?.message || "Google Sheet created successfully.");
      setModalOpen(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      setError(err.message || "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this Google Sheet configuration?")) return;
    setError("");
    try {
      const res = await apihubDelete(
        `/getInteractedCustomerDetail/deletegooglesheetconfig/${id}`,
      );
      if (res?.success === false) {
        throw new Error(res.message || "Delete failed");
      }
      setMessage(res?.message || "Deleted");
      load();
    } catch (err) {
      setError(err.message || "Failed to delete config");
    }
  };

  return (
    <div className="biz-panel-page biz-smsconfig-page">
      <div className="biz-panel-header biz-panel-header-row">
        <h1>
          <FaCog />
          Google Sheet Integration
        </h1>
        <button type="button" className="biz-btn-primary" onClick={openCreate}>
          <FaPlus /> Create Google Sheet
        </button>
      </div>

      {message && <p className="biz-banner biz-banner-success">{message}</p>}
      {error && !modalOpen && (
        <p className="biz-banner biz-banner-error">{error}</p>
      )}

      <div className="biz-table-wrap">
        {loading ? (
          <p className="biz-table-message">Loading Google Sheet configurations…</p>
        ) : (
          <table className="biz-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sheet Name</th>
                <th>Sheet URL</th>
                <th>Spreadsheet ID</th>
                <th className="biz-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="biz-table-empty">
                    No Google Sheet configurations yet.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row._id}>
                    <td>{(page - 1) * limit + idx + 1}</td>
                    <td>{row.sheetname}</td>
                    <td className="biz-table-mono">
                      {row.sheeturl ? (
                        <a
                          href={row.sheeturl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open sheet"
                        >
                          {row.sheeturl} <FaExternalLinkAlt />
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{row.spreadsheetid || "—"}</td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions">
                        <button
                          type="button"
                          className="biz-btn-icon"
                          title="Edit"
                          onClick={() => openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="biz-btn-icon biz-btn-icon-danger"
                          title="Delete"
                          onClick={() => onDelete(row._id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="biz-pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {modalOpen && (
        <div className="biz-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div
            className="biz-modal-card biz-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>
              {editId ? "Edit Google Sheet Configuration" : "Create Google Sheet"}
            </h2>
            {error && modalOpen && (
              <p className="biz-banner biz-banner-error">{error}</p>
            )}
            <form className="biz-form-grid" onSubmit={onSubmit}>
              <label>
                Sheet Name *
                <input
                  name="sheetname"
                  value={form.sheetname}
                  onChange={onChange}
                  required
                />
              </label>
              {editId && (
                <>
                  <label>
                    Sheet URL *
                    <input
                      name="sheeturl"
                      value={form.sheeturl}
                      onChange={onChange}
                      required
                    />
                  </label>
                  <label>
                    Spreadsheet ID
                    <input
                      name="spreadsheetid"
                      value={form.spreadsheetid}
                      onChange={onChange}
                    />
                  </label>
                </>
              )}
              <label className="biz-form-full">
                Description
                <textarea
                  name="description"
                  rows={3}
                  value={form.description}
                  onChange={onChange}
                />
              </label>
              {!editId && (
                <p className="biz-form-full" style={{ margin: 0, color: "#5f6368" }}>
                  You will sign in with Google on this site if this account has
                  not authorized Sheets access yet. The spreadsheet is created in
                  that Google account.
                </p>
              )}

              <div className="biz-modal-actions biz-form-full">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="biz-btn-primary" disabled={saving}>
                  {saving
                    ? editId
                      ? "Saving…"
                      : "Continuing…"
                    : editId
                      ? "Update"
                      : "Continue with Google"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleSheetConfig;
